import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line, Dot,
} from "recharts";
import "../../../assets/css/gcadmin/adminreports/AdminReportsPage.css";


/* ── Data ── */
const PERIODS = ["Last 6 Months", "Last 3 Months", "Last Year", "This Month"];

const DATA_6M = [
  { month: "Jul", issued: 45, returned: 38 },
  { month: "Aug", issued: 52, returned: 47 },
  { month: "Sep", issued: 78, returned: 63 },
  { month: "Oct", issued: 92, returned: 82 },
  { month: "Nov", issued: 65, returned: 70 },
  { month: "Dec", issued: 83, returned: 60 },
];

const DATA_3M = [
  { month: "Oct", issued: 92, returned: 82 },
  { month: "Nov", issued: 65, returned: 70 },
  { month: "Dec", issued: 83, returned: 60 },
];

const DATA_1Y = [
  { month: "Jan", issued: 30, returned: 28 },
  { month: "Feb", issued: 38, returned: 35 },
  { month: "Mar", issued: 55, returned: 50 },
  { month: "Apr", issued: 42, returned: 40 },
  { month: "May", issued: 60, returned: 55 },
  { month: "Jun", issued: 50, returned: 47 },
  { month: "Jul", issued: 45, returned: 38 },
  { month: "Aug", issued: 52, returned: 47 },
  { month: "Sep", issued: 78, returned: 63 },
  { month: "Oct", issued: 92, returned: 82 },
  { month: "Nov", issued: 65, returned: 70 },
  { month: "Dec", issued: 83, returned: 60 },
];

const DATA_1MO = [
  { month: "W1", issued: 22, returned: 18 },
  { month: "W2", issued: 28, returned: 24 },
  { month: "W3", issued: 19, returned: 22 },
  { month: "W4", issued: 25, returned: 20 },
];

const PERIOD_BAR_DATA = {
  "Last 6 Months": DATA_6M,
  "Last 3 Months": DATA_3M,
  "Last Year":     DATA_1Y,
  "This Month":    DATA_1MO,
};

const USER_GROWTH_6M = [
  { month: "Jul", users: 620 },
  { month: "Aug", users: 680 },
  { month: "Sep", users: 730 },
  { month: "Oct", users: 775 },
  { month: "Nov", users: 810 },
  { month: "Dec", users: 850 },
];

const USER_GROWTH_3M = [
  { month: "Oct", users: 775 },
  { month: "Nov", users: 810 },
  { month: "Dec", users: 850 },
];

const USER_GROWTH_1Y = [
  { month: "Jan", users: 400 }, { month: "Feb", users: 440 },
  { month: "Mar", users: 480 }, { month: "Apr", users: 510 },
  { month: "May", users: 550 }, { month: "Jun", users: 590 },
  { month: "Jul", users: 620 }, { month: "Aug", users: 680 },
  { month: "Sep", users: 730 }, { month: "Oct", users: 775 },
  { month: "Nov", users: 810 }, { month: "Dec", users: 850 },
];

const USER_GROWTH_1MO = [
  { month: "W1", users: 820 }, { month: "W2", users: 832 },
  { month: "W3", users: 841 }, { month: "W4", users: 850 },
];

const PERIOD_LINE_DATA = {
  "Last 6 Months": USER_GROWTH_6M,
  "Last 3 Months": USER_GROWTH_3M,
  "Last Year":     USER_GROWTH_1Y,
  "This Month":    USER_GROWTH_1MO,
};

const CATEGORY_DATA = [
  { name: "Computer Science", value: 45, color: "#1a5f7a" },
  { name: "AI / ML",          value: 20, color: "#2e8fa8" },
  { name: "Networking",       value: 12, color: "#4eb3cc" },
  { name: "Mathematics",      value: 15, color: "#90cfe0" },
  { name: "Electronics",      value: 8,  color: "#c2e4ed" },
];

const MOST_ISSUED = [
  { rank: 1, title: "Data Structures & Algorithms", count: 45, max: 45 },
  { rank: 2, title: "Introduction to ML",           count: 38, max: 45 },
  { rank: 3, title: "Operating System Concepts",    count: 32, max: 45 },
  { rank: 4, title: "Software Engineering",         count: 28, max: 45 },
  { rank: 5, title: "Database System Concepts",     count: 25, max: 45 },
];

/* ── Custom Tooltip ── */
const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="gcAdminRP-tooltip">
      <p className="gcAdminRP-tooltip__label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="gcAdminRP-tooltip__row" style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

const LineTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="gcAdminRP-tooltip">
      <p className="gcAdminRP-tooltip__label">{label}</p>
      <p className="gcAdminRP-tooltip__row" style={{ color: "#1a5f7a" }}>
        Users: <strong>{payload[0]?.value}</strong>
      </p>
    </div>
  );
};

/* ── Custom Pie Label ── */
const renderPieLabel = ({ cx, cy, midAngle, outerRadius, name, value }) => {
  const RADIAN = Math.PI / 180;
  const r = outerRadius + 28;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#4094ae" textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central" fontSize={11} fontWeight={500}>
      {name} {value}%
    </text>
  );
};

/* ══════════════════════════════════════════
   Main Page
══════════════════════════════════════════ */
const AdminReportsPage = () => {
  const [period, setPeriod]     = useState("Last 6 Months");
  const [dropdown, setDropdown] = useState(false);
  const [visible, setVisible]   = useState(false);
  const [chartsIn, setChartsIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 60);
    setTimeout(() => setChartsIn(true), 200);
  }, []);

  const barData  = PERIOD_BAR_DATA[period]  || DATA_6M;
  const lineData = PERIOD_LINE_DATA[period] || USER_GROWTH_6M;

  const handleExport = () => {
    alert("Export feature — connect to your backend API.");
  };

  return (
    <div className={`gcAdminRP-page ${visible ? "gcAdminRP-page--visible" : ""}`}>
      <Container fluid className="gcAdminRP-container">

        {/* ── Header ── */}
        <div className="gcAdminRP-header">
          <h2 className="gcAdminRP-title">Reports &amp; Analytics</h2>

          <div className="gcAdminRP-controls">
            {/* Period dropdown */}
            <div className="gcAdminRP-dropdown-wrap">
              <button
                className="gcAdminRP-period-btn"
                onClick={() => setDropdown(d => !d)}
              >
                {period}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {dropdown && (
                <div className="gcAdminRP-dropdown">
                  {PERIODS.map(p => (
                    <button
                      key={p}
                      className={`gcAdminRP-dropdown__item ${p === period ? "gcAdminRP-dropdown__item--active" : ""}`}
                      onClick={() => { setPeriod(p); setDropdown(false); }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Export button */}
            <button className="gcAdminRP-export-btn" onClick={handleExport}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <polyline points="7 10 12 15 17 10" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* ── Top 2 Charts ── */}
        <Row className="gy-3 gx-3 mb-3">

          {/* Bar Chart — Books Issued vs Returned */}
          <Col xs={12} lg={6}>
            <div className={`gcAdminRP-card ${chartsIn ? "gcAdminRP-card--in" : ""}`}
              style={{ transitionDelay: "0.05s" }}>
              <p className="gcAdminRP-card__title">Books Issued vs Returned</p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={barData} barCategoryGap="30%" barGap={4}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#edf2f5" strokeDasharray="0"/>
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#7badc0" }}
                    axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fontSize: 11, fill: "#7badc0" }}
                    axisLine={false} tickLine={false} domain={[0, 100]}/>
                  <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(64,148,174,0.06)" }}/>
                  <Bar dataKey="issued"   name="Issued"   fill="#1a5f7a" radius={[4,4,0,0]}/>
                  <Bar dataKey="returned" name="Returned" fill="#b8d8e4" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Col>

          {/* Pie Chart — Books by Category */}
          <Col xs={12} lg={6}>
            <div className={`gcAdminRP-card ${chartsIn ? "gcAdminRP-card--in" : ""}`}
              style={{ transitionDelay: "0.12s" }}>
              <p className="gcAdminRP-card__title">Books by Category</p>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={CATEGORY_DATA}
                    cx="42%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={95}
                    dataKey="value"
                    labelLine={true}
                    label={renderPieLabel}
                    strokeWidth={2}
                    stroke="#ffffff"
                  >
                    {CATEGORY_DATA.map((entry, i) => (
                      <Cell key={i} fill={entry.color}/>
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val, name) => [`${val}%`, name]}
                    contentStyle={{
                      borderRadius: 10, border: "1px solid #e8eef1",
                      fontSize: 12, color: "#1a3a4a"
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>

        {/* ── Bottom 2 Charts ── */}
        <Row className="gy-3 gx-3">

          {/* Line Chart — User Growth */}
          <Col xs={12} lg={6}>
            <div className={`gcAdminRP-card ${chartsIn ? "gcAdminRP-card--in" : ""}`}
              style={{ transitionDelay: "0.2s" }}>
              <p className="gcAdminRP-card__title">User Growth</p>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={lineData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#edf2f5" strokeDasharray="0"/>
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#7badc0" }}
                    axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fontSize: 11, fill: "#7badc0" }}
                    axisLine={false} tickLine={false}/>
                  <Tooltip content={<LineTooltip />}
                    cursor={{ stroke: "rgba(64,148,174,0.2)", strokeWidth: 1 }}/>
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="#1a5f7a"
                    strokeWidth={2.2}
                    dot={{ r: 4, fill: "#ffffff", stroke: "#1a5f7a", strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: "#1a5f7a" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Col>

          {/* Most Issued Books — Horizontal bar list */}
          <Col xs={12} lg={6}>
            <div className={`gcAdminRP-card ${chartsIn ? "gcAdminRP-card--in" : ""}`}
              style={{ transitionDelay: "0.28s" }}>
              <p className="gcAdminRP-card__title">Most Issued Books</p>

              <div className="gcAdminRP-most-list">
                {MOST_ISSUED.map((item, i) => (
                  <div
                    key={item.rank}
                    className={`gcAdminRP-most-row ${chartsIn ? "gcAdminRP-most-row--in" : ""}`}
                    style={{ transitionDelay: `${0.28 + i * 0.07}s` }}
                  >
                    <span className="gcAdminRP-most-rank">{item.rank}</span>
                    <div className="gcAdminRP-most-info">
                      <div className="gcAdminRP-most-title-row">
                        <span className="gcAdminRP-most-name">{item.title}</span>
                        <span className="gcAdminRP-most-count">{item.count}</span>
                      </div>
                      {/* Progress bar */}
                      <div className="gcAdminRP-most-bar-bg">
                        <div
                          className="gcAdminRP-most-bar-fill"
                          style={{
                            width: chartsIn ? `${(item.count / item.max) * 100}%` : "0%",
                            transitionDelay: `${0.35 + i * 0.07}s`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

export default AdminReportsPage;